import { UserPractice } from "../modules/user-management/types/user-management.types";

type PracticeSessionPayload = {
    practice: UserPractice | null;
};

enum PracticeSessionMessageType {
    Update = "session-update",
    Request = "session-request",
}

type PracticeSessionMessage =
    | {
          type: PracticeSessionMessageType.Update;
          payload: PracticeSessionPayload;
      }
    | { type: PracticeSessionMessageType.Request };

const PRACTICE_KEY = "practice";
const CHANNEL_NAME = "mantle-app-practice-context";

let channel: BroadcastChannel | null = null;
let isInitialized = false;

const canUseStorage =
    typeof window !== "undefined" &&
    typeof window.sessionStorage !== "undefined";

const canUseBroadcastChannel =
    typeof window !== "undefined" && "BroadcastChannel" in window;

function cleanupLegacyLocalStorage() {
    if (typeof window === "undefined") {
        return;
    }

    const legacyValue = window.localStorage.getItem(PRACTICE_KEY);

    if (legacyValue !== null) {
        if (canUseStorage && !window.sessionStorage.getItem(PRACTICE_KEY)) {
            window.sessionStorage.setItem(PRACTICE_KEY, legacyValue);
        }

        window.localStorage.removeItem(PRACTICE_KEY);
    }
}

function readPracticeFromStorage(): UserPractice | null {
    if (!canUseStorage) {
        return null;
    }

    const raw = window.sessionStorage.getItem(PRACTICE_KEY);

    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw) as UserPractice;
    } catch {
        return null;
    }
}

export function getPracticeSession(): PracticeSessionPayload {
    return {
        practice: readPracticeFromStorage(),
    };
}

function writePracticeSession(context: PracticeSessionPayload) {
    if (!canUseStorage) {
        return;
    }

    const { practice } = context;

    if (practice) {
        window.sessionStorage.setItem(PRACTICE_KEY, JSON.stringify(practice));
    } else {
        window.sessionStorage.removeItem(PRACTICE_KEY);
    }
}

function ensureChannel() {
    if (!canUseBroadcastChannel) {
        return;
    }

    if (!channel) {
        channel = new BroadcastChannel(CHANNEL_NAME);

        channel.onmessage = (event: MessageEvent<PracticeSessionMessage>) => {
            const message = event.data;

            if (!message) {
                return;
            }

            if (message.type === PracticeSessionMessageType.Update) {
                writePracticeSession(message.payload);
            } else if (message.type === PracticeSessionMessageType.Request) {
                const current = getPracticeSession();

                if (current.practice) {
                    channel?.postMessage({
                        type: PracticeSessionMessageType.Update,
                        payload: current,
                    });
                }
            }
        };
    }
}

export function initPracticeSessionSync() {
    if (isInitialized) {
        return;
    }

    cleanupLegacyLocalStorage();

    if (!canUseBroadcastChannel) {
        isInitialized = true;
        return;
    }

    ensureChannel();
    isInitialized = true;

    channel?.postMessage({ type: PracticeSessionMessageType.Request });
}

export function setPracticeSession(practice: UserPractice | null) {
    const next: PracticeSessionPayload = { practice };

    writePracticeSession(next);

    ensureChannel();
    channel?.postMessage({
        type: PracticeSessionMessageType.Update,
        payload: next,
    });
}

export function clearPracticeSession() {
    writePracticeSession({ practice: null });

    ensureChannel();
    channel?.postMessage({
        type: PracticeSessionMessageType.Update,
        payload: { practice: null },
    });
}
